import { t } from "@lingui/core/macro";
import graphql from "babel-plugin-relay/macro";
import { useAtom } from "jotai";
import { useState } from "react";
import { useFragment } from "react-relay";

import { AddPeopleSearch_Atom } from "@/features/AddPatrons/atoms/stores";
import { Button } from "@whoosh/design-system/src/components/controls";
import { StandardModal } from "@whoosh/design-system/src/components/overlay/StandardModal";
import { Box, VStack } from "@whoosh/design-system/src/components/primitive";

import type { DiningAttendeesModal_booking$key } from "./__generated__/DiningAttendeesModal_booking.graphql";
import { AttendeeAddContent } from "./components/AttendeeAddContent";
import { AttendeeListContent } from "./components/AttendeeListContent";
import { DiningAttendeesModalHeader } from "./components/DiningAttendeesModalHeader";
import type { Attendee } from "./components/types";
import {
  useAttendeeHandlers,
  useModeHandlers,
  useSaveHandler,
} from "./hooks/useAttendeeHandlers";
import {
  useAttendeeModalInitialization,
  useReplaceModeValidation,
} from "./hooks/useAttendeeModalInitialization";
import {
  useAttendeeDerivedState,
  useBookingAttendees,
  useDisplayAttendees,
  useIsDirty,
  useOwnerIdFromBooking,
} from "./hooks/useAttendeeState";

type Props = {
  isOpen: boolean;
  onClose: () => void;
  bookingRef: DiningAttendeesModal_booking$key;
};

type Mode =
  | { type: "list" }
  | { type: "add" }
  | { type: "replace"; targetPlayerId: string };

type InitialSnapshot = {
  ownerId: string | null;
  attendeeIds: string[];
};

export const DiningAttendeesModal = ({
  isOpen,
  onClose,
  bookingRef,
}: Props) => {
  const booking = useFragment(DiningAttendeesModalFragment, bookingRef);
  const [search, setSearch] = useAtom(AddPeopleSearch_Atom);

  const [attendees, setAttendees] = useState<Attendee[]>([]);
  const [ownerId, setOwnerId] = useState<string | null>(null);
  const [mode, setMode] = useState<Mode>({ type: "list" });
  const [initialSnapshot, setInitialSnapshot] =
    useState<InitialSnapshot | null>(null);

  const maxPartySize = booking?.partySize ?? null;
  const diningReservationHoldId = booking?.diningReservationHold?.id ?? null;

  const bookingAttendees = useBookingAttendees(booking);
  const ownerIdFromBooking = useOwnerIdFromBooking(booking);

  useAttendeeModalInitialization({
    isOpen,
    bookingAttendees,
    ownerIdFromBooking,
    setMode,
    setSearch,
    setAttendees,
    setOwnerId,
    setInitialSnapshot,
  });

  useReplaceModeValidation({
    mode,
    attendees,
    setMode,
  });

  const displayAttendees = useDisplayAttendees(attendees, ownerId);

  const {
    attendeeIds,
    selectedIdsForAddPatrons,
    targetAttendee,
    isAtCapacity,
    remainingSlots,
  } = useAttendeeDerivedState(attendees, ownerId, mode, maxPartySize);

  const isDirty = useIsDirty(ownerId, attendeeIds, initialSnapshot);

  const { handleToggleAttendee, handleRemoveAttendee } = useAttendeeHandlers({
    attendees,
    ownerId,
    mode,
    maxPartySize,
    setAttendees,
    setOwnerId,
    setMode,
    setSearch,
  });

  const [handleSave, isSaving] = useSaveHandler({
    booking,
    attendees,
    ownerId,
    diningReservationHoldId,
    onClose,
  });

  const { handleBackToList, handleOpenAdd, handleReplaceRequest } =
    useModeHandlers({
      setMode,
      setSearch,
    });

  const addModeTitle =
    mode.type === "replace"
      ? t`Replace ${targetAttendee?.displayName ?? ""}`
      : t`Add People`;

  const hasValidOwner = !!ownerId;
  const hasAttendees = attendees.length > 0;

  return (
    <StandardModal
      isOpen={isOpen}
      onClose={onClose}
      hideCloseButton={true}
      upperNode={
        <DiningAttendeesModalHeader
          modeType={mode.type}
          addModeTitle={addModeTitle}
          onClose={onClose}
          onBack={handleBackToList}
        />
      }
      disableContentScroll={true}
      content={
        <VStack flex={1} backgroundColor="surface-secondary">
          {mode.type === "list" ? (
            <AttendeeListContent
              attendees={attendees}
              displayAttendees={displayAttendees}
              ownerId={ownerId}
              remainingSlots={remainingSlots}
              maxPartySize={maxPartySize}
              isAtCapacity={isAtCapacity}
              onAddPress={handleOpenAdd}
              onReplace={handleReplaceRequest}
              onRemove={handleRemoveAttendee}
            />
          ) : (
            <AttendeeAddContent
              mode={mode.type === "replace" ? "replace" : "add"}
              search={search}
              setSearch={setSearch}
              selectedIds={selectedIdsForAddPatrons}
              isAtCapacity={isAtCapacity}
              onToggleAttendee={handleToggleAttendee}
            />
          )}
          {isDirty ? (
            <Box
              borderTopWidth={1}
              borderTopColor="surface-dividers"
              padding={6}
              backgroundColor="surface-primary"
            >
              <Button
                onPress={handleSave}
                isLoading={isSaving}
                isDisabled={!hasValidOwner || !hasAttendees || isSaving}
                accessibilityLabel={t`Save attendee changes`}
              >
                {t`Save Changes`}
              </Button>
            </Box>
          ) : null}
        </VStack>
      }
    />
  );
};

const DiningAttendeesModalFragment = graphql`
  fragment DiningAttendeesModal_booking on Booking {
    id
    startSlot {
      id
    }
    rounds {
      edges {
        node {
          player {
            id
            __typename
            fullName
            firstName
            lastName
            profileImage {
              # TODO(ENG-3436): Remove the publicId alias when legacy image props are retired.
              publicId: renderingKey
            }
          }
        }
      }
    }
    ownerClient {
      __typename
      ... on Member {
        id
      }
      ... on Customer {
        id
      }
    }
    ... on DiningBooking {
      partySize
      notesFromMember
      dietaryTags
      occasionTags
      status
      diningReservationHold {
        id
      }
    }
  }
`;

export { DiningAttendeesModalFragment };
